import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CasinoTheme, GameType } from '@tv-casino/shared';
import {
  CHIP_DENOMS,
  CHIP_SETS,
  CHIP_SET_IDS,
  DECKS,
  DECK_IDS,
  DEFAULT_CRAPS_RULES,
  DEFAULT_THEME,
  FELTS,
  GAME_LABELS,
  FELT_IDS,
  pocketColor,
  wheelOrder,
} from '@tv-casino/shared';
import { AlphaBadge, FreeDuringAlpha, PlayForFun } from '../components/AlphaNotice.js';
import { CardRow } from '../components/CardView.js';
import { IDLE_PATH as CAST_PATH } from '../components/CastButton.js';
import { CrapsDice } from '../components/CrapsDice.js';
import { CrapsTable } from '../components/CrapsTable.js';
import { DeckPreview } from '../components/DeckPreview.js';
import { GamePollCard } from '../components/GamePollCard.js';
import { MemberMarket } from '../components/MemberMarket.js';
import { PartyStrip } from '../components/PartyStrip.js';
import { gamePollModel } from '@tv-casino/viewmodels/components/gamePollModel.js';
import { memberMarketModel } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import { RouletteTable } from '../components/RouletteTable.js';
import { playerColor } from '../lib/playerColors.js';
import { chipVars, deckVars, feltVars, themeStyle } from '../lib/themeStyle.js';
import { ballAngle, polar, segmentPath } from '@tv-casino/viewmodels/lib/wheelGeometry.js';
import {
  BALLOT_MS,
  CRAPS_POINT,
  CRAPS_REEL,
  CRAPS_STACKS,
  REEL,
  REEL_MS,
  REEL_STANDINGS,
  ROLL_MS,
  ROULETTE_STACKS,
  SPIN_GAP_MS,
  SPIN_MS,
  NIGHT_STEPS,
  PARTY_MODES,
  TICKER,
  demoParty,
  DEMO_MEMBER,
  DEMO_SERIES,
  DEMO_ROUNDS,
  DEMO_LEDGER,
  DEMO_BALLOTS,
  DEMO_VOTER_ID,
  demoPoll,
  DRESS_HAND,
  FLOOR_ONLY,
  HOUSE_FACTS,
  HOUSE_LOOKS,
  HOUSE_QUESTIONS,
  lookOf,
  type ReelPhone,
  type ReelScene,
  crapsReelRoll,
  reelAt,
  reelIndexOf,
  rouletteReelPocket,
} from '@tv-casino/viewmodels/routes/landingModel.js';
import styles from './Landing.module.css';

/** AirPlay's screen-and-triangle glyph, the mark Apple TV mirroring wears. */
const AIRPLAY_PATH =
  'M6 22h12l-6-6zM21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v-2H3V5h18v12h-4v2h4c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z';

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * The furniture drifting behind the pitch. Positions are shares of the hero,
 * so everything keeps its place at any width; `wide` chips only come out when
 * the pitch and the TV sit side by side and there is felt between them.
 */
type Drift = { x: string; y: string; size: number; dur: number; delay: number; tilt?: number };
type ChipValue = 5 | 25 | 100 | 500 | 1000;

const FLOAT_CHIPS: readonly (Drift & { value: ChipValue; wide?: boolean })[] = [
  { value: 500, x: '30%', y: '2%', size: 54, dur: 8, delay: 0, tilt: -8, wide: true },
  { value: 1000, x: '40%', y: '30%', size: 46, dur: 9, delay: 3, tilt: 12, wide: true },
  { value: 5, x: '45%', y: '68%', size: 34, dur: 6, delay: 1, wide: true },
  { value: 100, x: '21%', y: '93%', size: 36, dur: 7, delay: 5, tilt: 20, wide: true },
  { value: 500, x: '49%', y: '90%', size: 40, dur: 8.5, delay: 2, tilt: -14, wide: true },
  { value: 100, x: '95%', y: '44%', size: 44, dur: 6.5, delay: 2 },
  { value: 25, x: '5%', y: '88%', size: 38, dur: 7.5, delay: 4 },
  { value: 25, x: '88%', y: '1%', size: 30, dur: 6, delay: 3.5, tilt: 16 },
  { value: 5, x: '47%', y: '10%', size: 30, dur: 7, delay: 1.5, tilt: -20, wide: true },
  { value: 1000, x: '84%', y: '95%', size: 34, dur: 8, delay: 6 },
];

const FLOAT_SUITS: readonly (Drift & { glyph: string })[] = [
  { glyph: '♠', x: '36%', y: '12%', size: 64, dur: 13, delay: 0 },
  { glyph: '♥', x: '10%', y: '56%', size: 52, dur: 11, delay: 4 },
  { glyph: '♦', x: '44%', y: '46%', size: 44, dur: 12, delay: 7 },
  { glyph: '♣', x: '2%', y: '6%', size: 40, dur: 14, delay: 2 },
];

const driftStyle = ({ x, y, size, dur, delay, tilt = 0 }: Drift) =>
  ({
    left: x,
    top: y,
    '--size': `${size}px`,
    '--tilt': `${tilt}deg`,
    animationDuration: `${dur}s`,
    animationDelay: `${-delay}s`,
  }) as CSSProperties;

const chipFace = (value: ChipValue) => (value >= 1000 ? `${value / 1000}K` : String(value));

/** Two chip towers flanking the last ask: somebody has been winning. */
const TOWERS: readonly (readonly ChipValue[])[] = [
  [25, 25, 100, 100, 500, 500, 1000],
  [5, 5, 25, 25, 100, 500],
];

/** The watermark each game wears on its card in the floor list. */
const GAME_MARKS: Partial<Record<GameType, string>> = {
  blackjack: '♠',
  holdem: '♣',
  baccarat: '♦',
  roulette: '◉',
  craps: '⚅',
  threecard: '♥',
  keno: '✱',
};

/** The chip each step of the night is numbered on, biggest first. */
const STEP_TONES = [500, 100, 25] as const;

/** The chip each figure on the house rail is struck on, in rail order. */
const FACT_TONES = [500, 100, 25, 5] as const;

/** The leader's stack, which every bar on the board is a share of. */
const TOP_CHIPS = Math.max(...REEL_STANDINGS.map((s) => s.chips));

/**
 * Ada's night on the shipped chip desk — the same `memberMarketModel` and
 * `MemberMarket` the phone lobby and the owner console render, fed a scripted
 * night instead of the REST tapes.
 */
const DEMO_DESK = memberMarketModel({
  me: DEMO_MEMBER,
  series: DEMO_SERIES,
  rounds: DEMO_ROUNDS,
  ledger: DEMO_LEDGER,
});

/** How long a stack takes to count up once the board comes into view. */
const COUNT_MS = 1_400;

/** Pools a game card's spotlight under the pointer. */
const spotlight = (e: ReactPointerEvent<HTMLElement>) => {
  const card = e.currentTarget;
  const box = card.getBoundingClientRect();
  card.style.setProperty('--mx', `${e.clientX - box.left}px`);
  card.style.setProperty('--my', `${e.clientY - box.top}px`);
};

/**
 * Nobody gets a moving front door they did not ask for. Read once, at mount:
 * the reel, the ball and the dice all hang off this single answer, so they
 * cannot end up half-still.
 */
const useStill = (): boolean =>
  useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )[0];

/** The demo wheel is European: 37 pockets, one zero, no '00'. */
const DEMO_WHEEL = wheelOrder('european');

/** Where the ball rides the track, and where it settles, in view-box units. */
const TRACK_RADIUS = 92;
const POCKET_RADIUS = 80;

/** The share of the spin the ball spends out on the track before it drops. */
const DROP_AT = 0.55;

const pocketClass = (pocket: number | '00'): string => {
  const tone = pocketColor(pocket);
  return tone === 'red' ? cx(styles.red) : tone === 'black' ? cx(styles.black) : cx(styles.green);
};

/**
 * The demo wheel: the same ring the TV draws, spinning through a scripted set
 * of pockets on its own clock. It is not reading a table — there is no table —
 * so the only honesty it owes is that the ball comes to rest on the number the
 * plate then names, which is what `ballAngle` is for.
 */
function DemoWheel() {
  const still = useStill();
  const [spin, setSpin] = useState(0);
  const [angle, setAngle] = useState(0);
  const [rolling, setRolling] = useState(false);
  const cumulative = useRef(0);

  // A beat of stillness before the first release, so the ring is legible as a
  // wheel before it starts moving.
  useEffect(() => {
    const start = window.setTimeout(() => setSpin(1), still ? 0 : 600);
    return () => window.clearTimeout(start);
  }, [still]);

  useEffect(() => {
    if (spin === 0) return;
    const index = DEMO_WHEEL.indexOf(rouletteReelPocket(spin));
    const target = ballAngle(cumulative.current, index, DEMO_WHEEL.length);
    cumulative.current = target;
    setAngle(target);
    if (still) return;
    setRolling(true);
    const settle = window.setTimeout(() => setRolling(false), SPIN_MS);
    const again = window.setTimeout(() => setSpin((s) => s + 1), SPIN_MS + SPIN_GAP_MS);
    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(again);
    };
  }, [spin, still]);

  const duration = still ? 0 : SPIN_MS;
  const landed = spin > 0 && !rolling;
  const pocket = landed ? rouletteReelPocket(spin) : null;
  const radius = rolling || spin === 0 ? TRACK_RADIUS : POCKET_RADIUS;

  return (
    <div className={styles.wheelWrap}>
      <svg className={styles.wheel} viewBox="0 0 200 200" aria-hidden="true">
        {/* The ring turns against the ball, as a real wheel does. */}
        <g
          className={styles.ring}
          style={{ transform: `rotate(${-angle * 0.35}deg)`, transitionDuration: `${duration}ms` }}
        >
          {DEMO_WHEEL.map((p, i) => (
            <path
              key={i}
              d={segmentPath(i, DEMO_WHEEL.length, 62, 97)}
              className={pocketClass(p)}
              stroke="rgba(232, 195, 122, 0.35)"
              strokeWidth="0.6"
            />
          ))}
          {DEMO_WHEEL.map((p, i) => {
            const seg = 360 / DEMO_WHEEL.length;
            const [tx, ty] = polar(100, 100, 80, i * seg + seg / 2);
            return (
              <text
                key={`t${i}`}
                x={tx}
                y={ty}
                className={styles.wheelNumber}
                textAnchor="middle"
                dominantBaseline="central"
                transform={`rotate(${angle * 0.35} ${tx} ${ty})`}
              >
                {String(p)}
              </text>
            );
          })}
        </g>
        <circle cx="100" cy="100" r="52" className={styles.hub} />
        <g
          className={styles.ballArm}
          style={{ transform: `rotate(${angle}deg)`, transitionDuration: `${duration}ms` }}
        >
          {/* The drop is its own transition, held until the ball has lost most
              of its speed — that is the spiral off the track. */}
          <g
            className={styles.ballDrop}
            style={{
              transform: `translateY(${-radius}px)`,
              transitionDuration: `${Math.round(duration * (1 - DROP_AT))}ms`,
              transitionDelay: `${Math.round(duration * DROP_AT)}ms`,
            }}
          >
            <circle cx="100" cy="100" r="5.2" className={styles.ball} />
          </g>
        </g>
      </svg>
      {/* The plate waits for the ball: naming the number mid-roll spoils it. */}
      <p className={styles.wheelPlate}>
        {pocket === null ? (
          <span className={styles.wheelWait}>Ball in play</span>
        ) : (
          <span className={cx(styles.pocket, pocketClass(pocket))} aria-label={`Pocket ${pocket}`}>
            {pocket}
          </span>
        )}
      </p>
    </div>
  );
}

/**
 * The demo dome, throwing one scripted hand of craps on a loop. It is the
 * shipped `CrapsDice`, driven by a roll counter exactly the way the TV and the
 * phone drive it — the front door gets the real dice or none.
 */
function DemoDice() {
  const still = useStill();
  const [roll, setRoll] = useState(0);

  useEffect(() => {
    // Under reduced motion the dome throws once and then holds the hand's last
    // roll: a front door should not churn pips at someone who asked it not to.
    if (still) {
      setRoll(CRAPS_REEL.length);
      return;
    }
    const next = window.setTimeout(() => setRoll((r) => r + 1), roll === 0 ? 500 : ROLL_MS);
    return () => window.clearTimeout(next);
  }, [roll, still]);

  const shown = crapsReelRoll(roll);
  return (
    <div className={styles.dome}>
      <CrapsDice
        rollNumber={roll}
        dice={roll === 0 ? null : [shown.dice[0], shown.dice[1]]}
        placeholder="Coming out"
        caption={roll === 0 ? null : shown.call}
      />
    </div>
  );
}

/**
 * Scale whatever is inside down until it fits the box it was given, never up.
 * The felts are the shipped components at their phone size — real cell heights
 * in real pixels — and the bezel is whatever share of the hero column the page
 * has to spare, so something has to give. A transform never feeds back into
 * layout, so the natural size measured here cannot move under the measurement.
 */
function Fit({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const outer = box.current;
    const content = inner.current;
    if (!outer || !content) return;
    const measure = () => {
      const bw = outer.clientWidth;
      const bh = outer.clientHeight;
      const iw = content.offsetWidth;
      const ih = content.offsetHeight;
      if (!bw || !bh || !iw || !ih) return;
      setScale(Math.min(1, bw / iw, bh / ih));
    };
    measure();
    // No ResizeObserver in jsdom: the one measurement is the whole behaviour
    // there, and a test asserting layout would be lying anyway.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={styles.fit} ref={box}>
      <div className={styles.fitInner} ref={inner} style={{ transform: `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

/**
 * The printed felt of a layout game, with the thing that decides it standing
 * beside it — the wheel next to the roulette layout, the dome next to the
 * craps layout, which is how both tables are laid out in a room. These are the
 * shipped `RouletteTable` and `CrapsTable` with no handlers passed, so they
 * render read-only exactly as the TV renders them, chips and all.
 */
function DemoLayout({ scene }: { scene: ReelScene }) {
  return (
    <Fit>
      <div className={styles.layout}>
        {scene.game === 'roulette' ? (
          <>
            <DemoWheel />
            <RouletteTable wheel="european" layout="wide" stacks={ROULETTE_STACKS} />
          </>
        ) : (
          <>
            <DemoDice />
            <CrapsTable rules={DEFAULT_CRAPS_RULES} point={CRAPS_POINT} stacks={CRAPS_STACKS} />
          </>
        )}
      </div>
    </Fit>
  );
}

/** Whatever the felt is showing this beat, drawn with the table's own parts. */
function DemoFelt({ scene }: { scene: ReelScene }) {
  if (scene.felt === 'layout') return <DemoLayout scene={scene} />;
  return (
    <Fit>
      <div className={styles.hands}>
        {scene.hands.map((hand) => (
          <div key={hand.label} className={styles.hand}>
            <CardRow cards={hand.cards} overlap={hand.overlap} />
            {/* Whose row it is and what it reads sit on one plate, like the seat
                plates on the felt — two stacked lines per hand is a line of text
                more than a 16:9 box has room for on a phone. */}
            <span className={styles.handPlate}>
              <b className={styles.handLabel}>{hand.label}</b>
              {hand.value && <span className={styles.handValue}>{hand.value}</span>}
            </span>
          </div>
        ))}
      </div>
    </Fit>
  );
}

/**
 * One of the two phones under the bezel: a seat at the table on the screen
 * above it. It holds that player's own cards where the game deals any, their
 * chip rail where the felt is the control, and the question their turn is
 * actually being asked — which is the whole point the page is making about
 * where the controls live.
 */
function DemoPhone({ phone }: { phone: ReelPhone }) {
  const hue = playerColor(phone.seatIndex);
  return (
    <figure className={styles.phone}>
      <span className={styles.phoneNotch} aria-hidden="true" />
      <figcaption className={styles.phoneTop}>
        <span className={styles.seatDot} style={{ background: hue }} aria-hidden="true" />
        <span className={styles.phoneName}>{phone.name}</span>
        <span className={styles.phoneChips}>{phone.chips.toLocaleString('en-US')}</span>
      </figcaption>

      <div className={styles.phoneBody}>
        {phone.hand && (
          <div className={styles.phoneHand}>
            <CardRow cards={phone.hand} />
          </div>
        )}
        {phone.rail && (
          <div className={styles.rail} aria-label="Chip rail">
            {phone.rail.map((chip) => (
              <span key={chip} className={cx(styles.chip, styles[`chip${chip}`])}>
                {chip}
              </span>
            ))}
          </div>
        )}
        <p className={styles.phonePrompt}>{phone.prompt}</p>
      </div>

      {/* The first button is the one the table is waiting on, so it is the lit
          one — the same way the phone lights the call it expects. */}
      <div className={styles.phoneActions}>
        {phone.actions.map((action, i) => (
          <span
            key={action}
            className={cx(styles.phoneAction, i === 0 && styles.phoneActionOn)}
            style={i === 0 ? { borderColor: hue } : undefined}
          >
            {action}
          </span>
        ))}
      </div>
    </figure>
  );
}

/**
 * The TV in the corner of the page, running the reel. The bezel, the tally
 * light and the join chip are the same furniture the real `/tv` screen wears,
 * because the promise of the product is "this appears on your television" and
 * a screenshot of a web page does not make that promise.
 */
function DemoTv({ scene }: { scene: ReelScene }) {
  return (
    <div className={styles.tv}>
      <div className={styles.bezel}>
        {/* Marquee bulbs chasing around the cabinet's frame. The screen paints
            over the middle of the strand, so only the rim of dots shows. */}
        <span className={styles.bulbs} aria-hidden="true" />
        <div className={styles.screen}>
          <div className={styles.screenTop}>
            <span className={styles.tally} aria-hidden="true" />
            <span className={styles.tableName}>Table 1 · {scene.name}</span>
            <span className={styles.joinChip}>
              Join · <b>VELV</b>
            </span>
          </div>

          {/* Keyed on the game so a change of table deals a fresh hand rather
              than morphing one felt into the next. */}
          <div className={styles.feltArea}>
            <DemoFelt key={scene.game} scene={scene} />
          </div>

          <p className={styles.call}>{scene.call}</p>

          <ul className={styles.seatRow}>
            {REEL_STANDINGS.map((s) => (
              <li key={s.name} className={styles.seat}>
                <span
                  className={styles.seatDot}
                  style={{ background: playerColor(s.seatIndex) }}
                  aria-hidden="true"
                />
                {s.name}
              </li>
            ))}
            <li className={cx(styles.seat, styles.seatOpen)}>Open seat</li>
          </ul>
        </div>
        <span className={styles.glare} aria-hidden="true" />
      </div>
      <span className={styles.stand} aria-hidden="true" />
    </div>
  );
}

/**
 * The dress-up band: quick looks, then the felt, the chips and the deck one
 * at a time, beside a slice of table wearing them. A pick re-dresses the whole
 * page, the TV up top included, the same way an owner's pick re-dresses every
 * screen in their casino. Nothing is saved; it is a fitting room.
 */
function DressTheHouse({
  theme,
  onTheme,
}: {
  theme: CasinoTheme;
  onTheme: (theme: CasinoTheme) => void;
}) {
  const look = lookOf(theme);
  const pick = (part: Partial<CasinoTheme>) => onTheme({ ...theme, ...part });
  return (
    <section className={cx(styles.band, styles.dress)} aria-labelledby="dress">
      <div className={cx(styles.dressCopy, styles.reveal)}>
        <h2 className={styles.bandTitle} id="dress">
          Dress the house
        </h2>
        <p className={styles.bandBody}>
          Pick the felt, the chips and the deck. Every table in the casino wears them, on the TV
          and on every phone. Try some on here: this whole page changes with you.
        </p>

        <div className={styles.looks} role="group" aria-label="House looks">
          {HOUSE_LOOKS.map((l) => (
            <button
              key={l.name}
              type="button"
              className={cx(styles.look, l === look && styles.lookOn)}
              style={themeStyle(l.theme)}
              aria-pressed={l === look}
              onClick={() => onTheme(l.theme)}
            >
              <span className={styles.lookChips} aria-hidden="true">
                {CHIP_DENOMS.map((d) => (
                  <span key={d} className={cx(styles.lookChip, styles[`tone${d}`])} />
                ))}
              </span>
              {l.name}
            </button>
          ))}
        </div>

        <p className={styles.pickTitle}>Felt · {FELTS[theme.felt].label}</p>
        <div className={styles.picks} role="group" aria-label="Felt">
          {FELT_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={cx(styles.pick, styles.feltPick, id === theme.felt && styles.pickOn)}
              style={feltVars(id)}
              aria-pressed={id === theme.felt}
              aria-label={FELTS[id].label}
              title={FELTS[id].label}
              onClick={() => pick({ felt: id })}
            />
          ))}
        </div>

        <p className={styles.pickTitle}>Chips · {CHIP_SETS[theme.chips].label}</p>
        <div className={styles.picks} role="group" aria-label="Chips">
          {CHIP_SET_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={cx(styles.pick, id === theme.chips && styles.pickOn)}
              style={chipVars(id)}
              aria-pressed={id === theme.chips}
              aria-label={CHIP_SETS[id].label}
              title={CHIP_SETS[id].label}
              onClick={() => pick({ chips: id })}
            >
              <span className={styles.lookChips} aria-hidden="true">
                {CHIP_DENOMS.map((d) => (
                  <span key={d} className={cx(styles.lookChip, styles[`tone${d}`])} />
                ))}
              </span>
            </button>
          ))}
        </div>

        <p className={styles.pickTitle}>Deck · {DECKS[theme.deck].label}</p>
        <div className={styles.picks} role="group" aria-label="Deck">
          {DECK_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={cx(styles.pick, id === theme.deck && styles.pickOn)}
              style={{ ...feltVars(theme.felt), ...deckVars(id) }}
              aria-pressed={id === theme.deck}
              aria-label={DECKS[id].label}
              title={DECKS[id].label}
              onClick={() => pick({ deck: id })}
            >
              <DeckPreview />
            </button>
          ))}
        </div>
      </div>

      {/* A slice of the table in whatever the visitor has on: a hand dealt
          on the felt and a stack of each chip on the rail. */}
      <figure
        className={cx(styles.dressFelt, styles.reveal)}
        style={{ '--i': 2 } as CSSProperties}
        aria-label="The table in this look"
      >
        <figcaption className={styles.deskCaption}>
          <span className={styles.deskDot} aria-hidden="true" />
          {look ? `${look.name} look` : 'Your look'} · {FELTS[theme.felt].label} felt
        </figcaption>
        <div className={styles.dressTable}>
          <CardRow cards={DRESS_HAND} />
          <div className={styles.dressRail} aria-hidden="true">
            {CHIP_DENOMS.map((d) => (
              <span key={d} className={styles.dressStack}>
                {[0, 1, 2].map((n) => (
                  <span
                    key={n}
                    className={cx(styles.dressChip, styles[`tone${d}`])}
                    style={{ color: `var(--chip${d}-ink)` }}
                  >
                    {n === 2 ? d : null}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </figure>
    </section>
  );
}

/**
 * The table vote, run on a demo table. Ada opened it; the other seats' ballots
 * land one at a time once the band scrolls into view, and the visitor holds
 * the last seat. The card is the shipped `GamePollCard` on the shipped
 * `gamePollModel`, tallied by the server's own `tallyGamePoll`, so the
 * tie-break a visitor can provoke here is the one a real table runs.
 */
function TableVote() {
  const still = useStill();
  const [animate] = useState(() => !still && typeof IntersectionObserver !== 'undefined');
  // Anywhere that cannot watch for the band (jsdom, reduced motion) opens on
  // every scripted ballot already cast, waiting on the visitor's.
  const [landed, setLanded] = useState(animate ? 0 : DEMO_BALLOTS.length);
  const [started, setStarted] = useState(!animate);
  const [mine, setMine] = useState<GameType | null>(null);
  const card = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = card.current;
    if (!animate || !el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        io.disconnect();
        setStarted(true);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  useEffect(() => {
    if (!started || landed >= DEMO_BALLOTS.length) return;
    const next = window.setTimeout(() => setLanded((n) => n + 1), BALLOT_MS);
    return () => window.clearTimeout(next);
  }, [started, landed]);

  const poll = demoPoll(landed, mine);
  const vm = gamePollModel(poll, { playerId: DEMO_VOTER_ID })!;
  // The visitor sits in the seat after the scripted four, and wears its colour.
  const seatOf = (playerId: string) => {
    const i = DEMO_BALLOTS.findIndex((b) => b.playerId === playerId);
    return i === -1 ? DEMO_BALLOTS.length : i;
  };

  return (
    <section className={cx(styles.band, styles.vote)} aria-labelledby="vote">
      <div className={cx(styles.voteCopy, styles.reveal)}>
        <h2 className={styles.bandTitle} id="vote">
          Let the table vote
        </h2>
        <p className={styles.bandBody}>
          Nobody has to be the one who picks. Open a vote between hands, every phone gets a
          ballot, and the table plays whatever wins. A tie keeps the table on the game it is
          already dealing.
        </p>
        <p className={styles.bandBody}>You have the last seat. Cast a vote and watch it count.</p>
        <ol className={styles.ballots} aria-label="Ballots cast" aria-live="polite">
          {poll.votes.map((vote) => (
            <li key={vote.playerId} className={styles.ballot}>
              <span
                className={styles.seatDot}
                style={{ background: playerColor(seatOf(vote.playerId)) }}
                aria-hidden="true"
              />
              <b className={styles.ballotName}>{vote.name}</b>
              <span className={styles.ballotGame}>{GAME_LABELS[vote.game]}</span>
            </li>
          ))}
        </ol>
      </div>

      <figure
        ref={card}
        className={cx(styles.votePhone, styles.reveal)}
        style={{ '--i': 2 } as CSSProperties}
        aria-label="Your phone"
      >
        <figcaption className={styles.deskCaption}>
          <span className={styles.deskDot} aria-hidden="true" />
          Your phone · seat {DEMO_BALLOTS.length + 1}
        </figcaption>
        <GamePollCard vm={vm} onVote={setMine} />
      </figure>
    </section>
  );
}

/**
 * A chip total that counts up from zero the first time it scrolls into view,
 * the way a cage counts out a cash-out. Anywhere it cannot watch for that
 * (jsdom, reduced motion) it simply prints the number.
 */
function CountUp({ value }: { value: number }) {
  const still = useStill();
  const [animate] = useState(() => !still && typeof IntersectionObserver !== 'undefined');
  const [shown, setShown] = useState(animate ? 0 : value);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!animate || !el) return;
    let raf = 0;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / COUNT_MS);
        setShown(Math.round(value * (1 - (1 - t) ** 3)));
        if (t < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    });
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [animate, value]);

  if (!animate) return <span>{value.toLocaleString('en-US')}</span>;

  // The count is for the eye; a screen reader gets the real total at once.
  return (
    <>
      <span ref={ref} aria-hidden="true">
        {shown.toLocaleString('en-US')}
      </span>
      <span className={styles.srOnly}>{value.toLocaleString('en-US')}</span>
    </>
  );
}

/**
 * The front door. A casino is the main path — the owner signs in, opens the
 * house, and puts a table on the TV; chips and stats then follow every player
 * across the night. The anonymous quick table stays as a secondary path for
 * a one-off hand with no account and nothing to remember.
 *
 * The page sells the table by running one: a bezel cycling the five felts,
 * drawn with the same card, dice and wheel parts the live TV uses. Everything
 * inside it is scripted (`landingModel.ts`) — no socket, no server, nothing
 * that can fail to load and leave the front door blank.
 */
export function Landing() {
  const still = useStill();
  const [tick, setTick] = useState(0);
  // Bumped whenever a visitor picks a channel, so the dwell timer restarts
  // rather than yanking the felt away a moment after they chose it.
  const [epoch, setEpoch] = useState(0);
  // Fixed at mount, so the demo strip counts down from 31:12 like a real TV.
  const [partyDemo] = useState(() => demoParty(Date.now()));
  const [theme, setTheme] = useState<CasinoTheme>(DEFAULT_THEME);

  useEffect(() => {
    if (still) return;
    const t = window.setInterval(() => setTick((n) => n + 1), REEL_MS);
    return () => window.clearInterval(t);
  }, [still, epoch]);

  // The sections below the hero rise in as they reach the viewport. Anything
  // that cannot watch for that (jsdom, reduced motion) is simply shown.
  const page = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = page.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(`.${styles.reveal}`));
    if (still || typeof IntersectionObserver === 'undefined') {
      for (const item of items) item.dataset.shown = '';
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.shown = '';
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    for (const item of items) io.observe(item);
    return () => io.disconnect();
  }, [still]);

  const scene = reelAt(tick);
  const tuneTo = (game: GameType) => {
    setTick(reelIndexOf(game));
    setEpoch((e) => e + 1);
  };
  return (
    <main className={styles.page} ref={page} style={themeStyle(theme)}>
      {/* The marquee over the door: looping, decorative, and silent to screen
          readers, who get the pitch below instead of a carnival barker. */}
      <div className={styles.ticker} aria-hidden="true">
        <div className={styles.tickerTrack}>
          {[0, 1].map((run) => (
            <div className={styles.tickerRun} key={run}>
              {TICKER.flatMap((item) => [
                <span key={item} className={styles.tickerItem}>
                  {item}
                </span>,
                <span key={`${item}-star`} className={styles.tickerStar}>
                  ★
                </span>,
              ])}
            </div>
          ))}
        </div>
      </div>

      <section className={styles.hero}>
        {/* Chips and big card numerals drifting behind the pitch, felt-level
            furniture rather than content — clipped to the hero, never read. */}
        <div className={styles.heroFloat} aria-hidden="true">
          <span className={cx(styles.floatNum, styles.floatNumA)}>21</span>
          <span className={cx(styles.floatNum, styles.floatNumB)}>7</span>
          {FLOAT_SUITS.map((suit) => (
            <span key={suit.glyph} className={styles.floatSuit} style={driftStyle(suit)}>
              {suit.glyph}
            </span>
          ))}
          {FLOAT_CHIPS.map((chip, i) => (
            <span
              key={i}
              className={cx(styles.floatChip, styles[`tone${chip.value}`], chip.wide && styles.floatWide)}
              style={driftStyle(chip)}
            >
              {chipFace(chip.value)}
            </span>
          ))}
        </div>

        <div className={styles.pitch}>
          <img className={styles.mark} src="/favicon.svg" alt="" width={148} height={148} />
          <p className={styles.kicker}>House party</p>
          <h1 className={styles.title}>
            TV Casino <span className={styles.neon}>Night</span>
            <AlphaBadge />
          </h1>
          <p className={styles.sub}>
            Put the game on the TV, hand out the code, and the living room is the floor. The TV
            deals, the phones decide, and every chip follows its player all night.
          </p>
          <div className={styles.actions}>
            <Link className={cx(styles.button, styles.primary)} to="/casino">
              Run the house (owner)
            </Link>
            <Link className={styles.button} to="/play">
              Join with a code (phone)
            </Link>
          </div>
          <p className={styles.facts}>Nothing to install · no sign-in to play</p>
          <ul className={styles.worksWith} aria-label="Works with">
            <li className={styles.worksItem}>
              <svg className={styles.worksIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d={CAST_PATH} />
              </svg>
              Chromecast · Google TV
            </li>
            <li className={styles.worksItem}>
              <svg className={styles.worksIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d={AIRPLAY_PATH} />
              </svg>
              Apple TV (AirPlay)
            </li>
            <li className={styles.worksItem}>
              <Link className={styles.worksLink} to="/roku">
                <svg className={styles.worksIcon} viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="2" y="5" width="15" height="14" rx="3.5" fill="none" strokeWidth="2.2" stroke="currentColor" />
                  <circle cx="20.5" cy="16.5" r="2.4" />
                </svg>
                Roku channel
              </Link>
            </li>
          </ul>
          <FreeDuringAlpha className={styles.free} />
        </div>

        <div className={styles.screenSide}>
          <DemoTv scene={scene} />

          {/* Two seats at the table above them, joined to it by a lead down
              off the cabinet — the claim the page is making, drawn. */}
          <div className={styles.phones} aria-label="Two phones at this table">
            {scene.phones.map((phone) => (
              <DemoPhone key={phone.name} phone={phone} />
            ))}
          </div>

          <div className={styles.strip} role="group" aria-label="What's on the TV">
            {REEL.map((s, i) => (
              <button
                key={s.game}
                type="button"
                className={cx(styles.channel, s.game === scene.game && styles.channelOn)}
                aria-pressed={s.game === scene.game}
                onClick={() => tuneTo(s.game)}
              >
                <span className={styles.channelNum}>{i + 1}</span>
                <span className={styles.channelName}>{s.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* The house in four numbers, each on a chip off the rail. */}
      <ul className={styles.houseFacts} aria-label="The house in numbers">
        {HOUSE_FACTS.map((fact, i) => (
          <li
            key={fact.label}
            className={cx(styles.houseFact, styles.reveal)}
            style={{ '--i': i } as CSSProperties}
          >
            <span
              className={cx(styles.houseFactChip, styles[`tone${FACT_TONES[i % FACT_TONES.length]}`])}
            >
              {fact.value}
            </span>
            <span className={styles.houseFactLabel}>{fact.label}</span>
          </li>
        ))}
      </ul>

      <section className={styles.band} aria-labelledby="games">
        <h2 className={styles.bandTitle} id="games">
          The floor
        </h2>
        <p className={styles.bandBody}>
          Every table the house deals. The one the TV up top is showing wears the light.
        </p>
        <div className={styles.games}>
          {REEL.map((s, i) => {
            const onAir = s.game === scene.game;
            return (
              <article
                key={s.game}
                className={cx(styles.game, onAir && styles.gameOn, styles.reveal)}
                style={{ '--i': i } as CSSProperties}
                onPointerMove={spotlight}
              >
                <span className={styles.gameMark} aria-hidden="true">
                  {GAME_MARKS[s.game]}
                </span>
                <h3 className={styles.gameName}>{s.name}</h3>
                <p className={styles.gameHook}>{s.hook}</p>
                <p className={styles.gameBody}>{s.blurb}</p>
                {onAir && (
                  <div className={styles.gameFoot}>
                    <span className={styles.onAir}>On air</span>
                  </div>
                )}
              </article>
            );
          })}
          {FLOOR_ONLY.map((g, i) => (
            <article
              key={g.game}
              className={cx(styles.game, styles.reveal)}
              style={{ '--i': REEL.length + i } as CSSProperties}
              onPointerMove={spotlight}
            >
              <span className={styles.gameMark} aria-hidden="true">
                {GAME_MARKS[g.game]}
              </span>
              <h3 className={styles.gameName}>{g.name}</h3>
              <p className={styles.gameHook}>{g.hook}</p>
              <p className={styles.gameBody}>{g.blurb}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.band} aria-labelledby="run">
        <h2 className={styles.bandTitle} id="run">
          How the night runs
        </h2>
        <ol className={styles.steps}>
          {NIGHT_STEPS.map((step, i) => (
            <li
              key={step.title}
              className={cx(styles.step, styles.reveal)}
              style={{ '--i': i } as CSSProperties}
            >
              <span
                className={cx(styles.stepChip, styles[`tone${STEP_TONES[i % STEP_TONES.length]}`])}
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepBody}>{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className={cx(styles.band, styles.party)} aria-labelledby="party">
        <div className={cx(styles.partyCopy, styles.reveal)}>
          <h2 className={styles.bandTitle} id="party">
            Party mode
          </h2>
          <p className={styles.bandBody}>
            Can't pick a game? Don't. Line up the tables you want and the TV works through them on
            its own. It only ever switches between hands, so nobody gets cut off mid-deal.
          </p>
          <ul className={styles.partyModes}>
            {PARTY_MODES.map((mode, i) => (
              <li
                key={mode.title}
                className={cx(styles.partyMode, styles.reveal)}
                style={{ '--i': i + 1 } as CSSProperties}
              >
                <h3 className={styles.stepTitle}>{mode.title}</h3>
                <p className={styles.stepBody}>{mode.body}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* The strip the TV runs over the felt, the shipped component on a
            demo schedule. */}
        <figure
          className={cx(styles.partyTv, styles.reveal)}
          style={{ '--i': 3 } as CSSProperties}
          aria-label="Party mode on the TV"
        >
          <figcaption className={styles.deskCaption}>
            <span className={styles.deskDot} aria-hidden="true" />
            On the TV · three hours, four games
          </figcaption>
          <PartyStrip party={partyDemo} />
        </figure>
      </section>

      <TableVote />

      <DressTheHouse theme={theme} onTheme={setTheme} />

      <section className={cx(styles.band, styles.bankroll)} aria-labelledby="chips">
        <div className={styles.bankrollSide}>
          <div className={cx(styles.bankrollCopy, styles.reveal)}>
            <h2 className={styles.bandTitle} id="chips">
              One night, one bankroll
            </h2>
            <p className={styles.bandBody}>
              Join the house once and your chips go where you go. Cash out of blackjack, walk to
              craps, sit back down at roulette. Every hand prints as a trade and every chip lands
              on the ledger, so when the night ends the board has the real numbers, not anyone's
              memory of them.
            </p>
          </div>

          {/* The chip card off the top of Ada's phone, the one number a player
              watches all night. */}
          <figure
            className={cx(styles.chipCard, styles.reveal)}
            style={{ '--i': 1 } as CSSProperties}
            aria-label="Ada's phone"
          >
            <figcaption className={styles.chipCardLabel}>Ada's phone</figcaption>
            <span className={styles.chipCardKicker}>Chips</span>
            <span className={styles.chipCardValue}>
              <CountUp value={DEMO_MEMBER.chips} />
            </span>
            <span className={styles.chipCardNote}>
              Bought in for {DEMO_MEMBER.buyIn.toLocaleString('en-US')} ·{' '}
              <span className={styles.up}>
                +{(DEMO_MEMBER.chips - DEMO_MEMBER.buyIn).toLocaleString('en-US')}
              </span>{' '}
              tonight
            </span>
          </figure>

          <ol
            className={cx(styles.board, styles.reveal)}
            style={{ '--i': 2 } as CSSProperties}
            aria-label="A night's standings"
          >
            {REEL_STANDINGS.map((s, i) => {
              const up = s.delta >= 0;
              return (
                <li
                  key={s.name}
                  className={cx(styles.boardRow, i === 0 && styles.boardLead)}
                  style={{ '--row': i, '--share': s.chips / TOP_CHIPS } as CSSProperties}
                >
                  <span className={styles.boardRank}>
                    {i === 0 ? (
                      <span className={styles.crown} role="img" aria-label="1, chip leader">
                        ♛
                      </span>
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span
                    className={styles.seatDot}
                    style={{ background: playerColor(s.seatIndex) }}
                    aria-hidden="true"
                  />
                  <span className={styles.boardName}>{s.name}</span>
                  <span className={styles.boardNote}>
                    <span className={cx(styles.boardDelta, up ? styles.up : styles.down)}>
                      <span aria-hidden="true">{up ? '▲' : '▼'}</span>
                      <span className={styles.srOnly}>{up ? 'up' : 'down'}</span>{' '}
                      {Math.abs(s.delta).toLocaleString('en-US')}
                    </span>
                    {' · '}
                    {s.tables}
                  </span>
                  <span className={styles.boardChips}>
                    <CountUp value={s.chips} />
                  </span>
                  <span className={styles.boardBar} aria-hidden="true" />
                </li>
              );
            })}
          </ol>
        </div>

        {/* Ada's chip desk: the quote and the line, then the last three hands
            as trades and the chip ledger they wrote. */}
        <div className={cx(styles.deskWrap, styles.reveal)} style={{ '--i': 3 } as CSSProperties}>
          <p className={styles.deskCaption}>
            <span className={styles.deskDot} aria-hidden="true" />
            Ada's chip desk · last three hands
          </p>
          <MemberMarket vm={DEMO_DESK} />
        </div>
      </section>

      <section className={cx(styles.band, styles.faq)} aria-labelledby="questions">
        <h2 className={styles.bandTitle} id="questions">
          Questions at the door
        </h2>
        <div className={styles.questions}>
          {HOUSE_QUESTIONS.map((q, i) => (
            <details
              key={q.question}
              className={cx(styles.question, styles.reveal)}
              style={{ '--i': i } as CSSProperties}
            >
              <summary className={styles.questionAsk}>{q.question}</summary>
              <p className={styles.questionAnswer}>{q.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={styles.closer}>
        {/* The sign over the cage: a ring of chasing bulbs inside the border. */}
        <span className={cx(styles.bulbs, styles.closerBulbs)} aria-hidden="true" />
        {TOWERS.map((tower, t) => (
          <div
            key={t}
            className={cx(styles.tower, t === 0 ? styles.towerL : styles.towerR)}
            aria-hidden="true"
          >
            {tower.map((value, i) => (
              <span key={i} className={cx(styles.disc, styles[`tone${value}`])} />
            ))}
          </div>
        ))}
        <h2 className={cx(styles.closerTitle, styles.reveal)}>Open the house</h2>
        <p className={cx(styles.closerSub, styles.reveal)} style={{ '--i': 1 } as CSSProperties}>
          The bulbs are lit and the felt is clean. Somebody has to be the house.
        </p>
        <div className={cx(styles.actions, styles.reveal)} style={{ '--i': 2 } as CSSProperties}>
          <Link className={cx(styles.button, styles.primary)} to="/casino">
            Start a casino
          </Link>
          <Link className={styles.button} to="/play">
            Join a table
          </Link>
        </div>
        <p className={styles.aside}>
          No account handy?{' '}
          <Link className={styles.quiet} to="/host">
            Open a quick table
          </Link>{' '}
          One anonymous room, chips reset when it closes.
        </p>
      </section>

      <PlayForFun free className={styles.fine} />
    </main>
  );
}
